import dayjs from 'dayjs';
import { PROGRESS_COLOR_CONFIG } from './constants';

export const getProgressColor = (
    progress: number,
): { clampedValue: number; color: string; textColor: string } => {
    const clampedProgress = Math.max(0, Math.min(100, progress ?? 0));

    const match = PROGRESS_COLOR_CONFIG.find(
        item => clampedProgress >= item.min,
    );

    if (match) {
        return {
            clampedValue: clampedProgress,
            color: match.color,
            textColor: match.textColor,
        };
    }

    return {
        clampedValue: clampedProgress,
        color: 'progressGray',
        textColor: 'progressGray',
    };
};

export const isSurveyInDivisionalManagerReview = (
    survey: { status?: string | null } | null,
): boolean => survey?.status === 'DivisionalManagerReview';

export const isSurveyStoreManagerEditingLocked = (
    survey: { status?: string | null } | null,
): boolean =>
    isSurveyInDivisionalManagerReview(survey) ||
    survey?.status === 'Completed' ||
    survey?.status === 'Cancelled';

export const isSurveyCancellable = (
    survey: { status?: string | null } | null,
): boolean =>
    !!survey?.status &&
    survey.status !== 'Completed' &&
    survey.status !== 'Cancelled';

export const getSubmitToDivisionalManagerTooltipLabel = (
    submittedToDivisionalManager: boolean,
    submitIncomplete: boolean,
): string => {
    if (submittedToDivisionalManager) return '';
    if (submitIncomplete)
        return 'Survey needs to be completed in order to submit it to the Divisional Manager';
    return '';
};

export function formatChampionSubmitToDivisionalManagerCopy(
    championDisplayName: string,
    storeNo?: string | null,
    storeName?: string | null,
): string {
    const trimmedChampionName = championDisplayName.trim() || 'The champion';
    const trimmedStoreNumber = storeNo != null ? String(storeNo).trim() : '';
    const trimmedStoreName = storeName != null ? storeName.trim() : '';

    let storePart = '';
    if (trimmedStoreNumber && trimmedStoreName) {
        storePart = ` (${trimmedStoreNumber} - ${trimmedStoreName})`;
    } else if (trimmedStoreNumber) {
        storePart = ` (${trimmedStoreNumber})`;
    } else if (trimmedStoreName) {
        storePart = ` (${trimmedStoreName})`;
    }

    return `${trimmedChampionName}${storePart} needs to submit this survey to the Divisional Manager.`;
}

export const mapStatusToDisplay = (status: string): string => {
    const statusMap: Record<string, string> = {
        NotStarted: 'Pending',
        InProgress: 'In Progress',
        Completed: 'Completed',
        Overdue: 'Overdue',
        Cancelled: 'Cancelled',
        DivisionalManagerReview: 'Divisional Manager Review',
    };
    return statusMap[status] || status;
};

export const mapDisplayStatusToBackend = (displayStatus: string): string => {
    const statusMap: Record<string, string> = {
        Pending: 'NotStarted',
        'In Progress': 'InProgress',
        Completed: 'Completed',
        Overdue: 'Overdue',
        Cancelled: 'Cancelled',
        'Divisional Manager Review': 'DivisionalManagerReview',
        'Awaiting Divisional Manager Approval': 'DivisionalManagerReview',
    };
    return statusMap[displayStatus] || displayStatus;
};

export const VALID_BACKEND_SURVEY_STATUSES: readonly string[] = [
    'NotStarted',
    'InProgress',
    'Completed',
    'Overdue',
    'Cancelled',
    'DivisionalManagerReview',
];

export const getSurveyStatus = (
    backendStatus: string,
    dueBy: string | Date,
): string => {
    const isOverdue =
        dayjs().isAfter(dayjs(dueBy)) &&
        backendStatus !== 'Completed' &&
        backendStatus !== 'Cancelled';

    if (isOverdue) return 'Overdue';

    return mapStatusToDisplay(backendStatus);
};

export const formatManagerName = (manager: {
    firstName: string;
    lastName: string;
}): string => {
    return `${manager.firstName || ''} ${manager.lastName || ''}`.trim();
};

export { getErrorMessage } from '../../helpers/getErrorMessage';

export const getNumberInputValue = (
    price: number | string | null | undefined,
    isNA: boolean,
): number | undefined => {
    if (isNA) {
        return undefined;
    }
    if (price === null || price === undefined) {
        return undefined;
    }
    if (typeof price === 'number') {
        // Ensure it's a valid finite number
        return Number.isFinite(price) ? price : undefined;
    }
    if (typeof price === 'string') {
        const trimmed = price.trim();
        if (trimmed === '') {
            return undefined;
        }
        const numericPrice = parseFloat(trimmed);
        return Number.isNaN(numericPrice) || !Number.isFinite(numericPrice)
            ? undefined
            : numericPrice;
    }
    const numericPrice = Number(price);
    return Number.isNaN(numericPrice) || !Number.isFinite(numericPrice)
        ? undefined
        : numericPrice;
};

type SurveyWithDivisionalManagerProgress = {
    dmActionedItemsCount?: number | null;
    dmActionableItemsCount?: number | null;
};

export type SurveyScopeDisplaySource = {
    advertisingZoneId?: string | null;
    advertisingZone?: {
        name?: string | null;
        stores?: Array<{
            id?: string | null;
            storeNo?: string | null;
            name?: string | null;
        }> | null;
    } | null;
    storeId?: string | null;
    store?: {
        id?: string | null;
        name?: string | null;
        storeNo?: string | null;
    } | null;
};

export const getSurveyStoreId = (
    survey: SurveyScopeDisplaySource | null | undefined,
): string | undefined => {
    const directStoreId = survey?.storeId?.trim();
    if (directStoreId) {
        return directStoreId;
    }
    const nestedStoreId = survey?.store?.id?.trim();
    if (nestedStoreId) {
        return nestedStoreId;
    }
    return undefined;
};

export const getSurveyAdvertisingZoneId = (
    survey: SurveyScopeDisplaySource | null | undefined,
): string | undefined => {
    const advertisingZoneId = survey?.advertisingZoneId?.trim();
    if (advertisingZoneId) {
        return advertisingZoneId;
    }
    return undefined;
};

export const isStoreScopedSurvey = (
    survey: SurveyScopeDisplaySource | null | undefined,
): boolean => {
    return Boolean(getSurveyStoreId(survey));
};

export const getSurveyScopeLabel = (
    survey: SurveyScopeDisplaySource | null | undefined,
): string => {
    if (!survey) {
        return 'Unknown';
    }

    if (getSurveyStoreId(survey)) {
        const storeNo = survey.store?.storeNo?.trim();
        const storeName = survey.store?.name?.trim();
        if (storeNo && storeName) {
            return `${storeNo} - ${storeName}`;
        }
        if (storeName) {
            return storeName;
        }
        if (storeNo) {
            return storeNo;
        }
        return 'Unknown';
    }

    return survey.advertisingZone?.name?.trim() || 'Unknown';
};

export const getSurveyScopeStores = (
    survey: SurveyScopeDisplaySource | null | undefined,
): Array<{
    id?: string | null;
    storeNo?: string | null;
    name?: string | null;
}> => {
    if (!survey) {
        return [];
    }

    const storeId = getSurveyStoreId(survey);
    if (storeId) {
        return [
            {
                id: storeId,
                storeNo: survey.store?.storeNo,
                name: survey.store?.name,
            },
        ];
    }

    return survey.advertisingZone?.stores ?? [];
};

export const getSurveyCompetitorScopeInput = (
    survey: SurveyScopeDisplaySource | null | undefined,
): { advertisingZoneId?: string; storeId?: string } => {
    const storeId = getSurveyStoreId(survey);
    if (storeId) {
        return { storeId };
    }

    const advertisingZoneId = getSurveyAdvertisingZoneId(survey);
    if (advertisingZoneId) {
        return { advertisingZoneId };
    }

    return {};
};

export const getSurveyDivisionalManagerProgress = (
    survey: SurveyWithDivisionalManagerProgress,
): {
    divisionalManagerActioned: number;
    divisionalManagerActionable: number;
    divisionalManagerPercent: number;
} => {
    const divisionalManagerActionable = Math.max(
        0,
        survey?.dmActionableItemsCount ?? 0,
    );
    const divisionalManagerActioned = Math.max(
        0,
        Math.min(
            survey?.dmActionedItemsCount ?? 0,
            divisionalManagerActionable,
        ),
    );
    const divisionalManagerPercent =
        divisionalManagerActionable > 0
            ? Math.round(
                  (divisionalManagerActioned / divisionalManagerActionable) *
                      100,
              )
            : 0;
    return {
        divisionalManagerActioned,
        divisionalManagerActionable,
        divisionalManagerPercent,
    };
};
